import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import CommonSelect from "../../../core/common/commonSelect";
import { DatePicker, Input, TimePicker } from "antd";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import dayjs, { Dayjs } from "dayjs";
import ReactDatePicker from "react-datepicker";
import {
  postOvertime,
  getSelfOvertime,
  getAllOvertime,
  updateOvertimeStatus,
  OvertimeRequest,
  OvertimeUpdate,
  OvertimeRecord,
  getOvertimeWidgets,
} from "../../../core/api/overtime";
import useGetEmployees from "../../../core/api/useGetEmployees";
import { API_BASE_PATH } from "../../../environment";
import { toast } from "react-toastify";
import { Dropdown } from "react-bootstrap";
import { fetchEmployeeImage } from "../../../core/api/fetchEmployeeImage";

type Option = {
  label: string;
  value: string;
};

type WidgetCardProps = {
  title: string;
  value: string | number;
  icon: string;
  iconBg: string;
  labelTopCentre?: string;
};

type StatusDropdownProps = {
  value: string;
  onChange: (option: { label: string; value: string }) => void;
};

const OverTime = () => {
  const BASE_PATH = API_BASE_PATH;
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [viewMode, setViewMode] = useState<"date" | "month">("month");
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month());
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [totalHours, setTotalHours] = useState<string>("");
  const [noOfDays, setNoOfDays] = useState<Option | null>(null);
  const [reason, setReason] = useState<string>("");
  const [rejectReason, setRejectReason] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [currentEmployee, setCurrentEmployee] = useState<any>(null);
  const [widgetData, setWidgetData] = useState<any>({});
  const [searchText, setSearchText] = useState<string>("");
  const [tableData, setTableData] = useState<any[]>([]); // Data for table
  const empToken = useSelector((state: any) => state.auth.token);
  // Get role from Redux store
  const role = useSelector((state: any) => state.auth.role) || "Employee";
  // Check if user is admin or manager
  const isAdminOrManager = role === "Admin" || role === "Manager";
  const { employees, isLoading: loadingEmployees } = useGetEmployees(empToken);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [notify, setNotify] = useState<string | null>(null);
  const [empImages, setEmpImages] = useState<Record<string, string>>({});
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.status !== "pending", // ✅ disable if not pending
    }),
  };

  // Month names for filter
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const toggleMonthDropdown = () => {
    setIsMonthDropdownOpen(!isMonthDropdownOpen);
  };

  // Status filter options
  const statusOptions: Option[] = [
    { value: "all", label: "All Data" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  // Days options
  const daysOptions: Option[] = [
    { value: "Select", label: "Select" },
    { value: "Full Day", label: "Full Day" },
    { value: "Half Day", label: "Half Day" },
  ];

  const employeeNameMap = useMemo(() => {
    const map: Record<
      string,
      { name: string; department: string; role: string }
    > = {};
    if (!Array.isArray(employees)) return map; // safety check
    employees.forEach((emp) => {
      map[emp.employee_id] = {
        name: `${emp.firstname} ${emp.lastname}`,
        department: emp.department_name || "Unknown Department",
        role: emp.role_name || "Unknown Role",
      };
    });
    return map;
  }, [employees]);

  // Calculate total hours
  const calculateTotalHours = () => {
    if (
      !startTime ||
      !endTime ||
      !selectedDate ||
      !dayjs.isDayjs(startTime) ||
      !dayjs.isDayjs(endTime) ||
      !dayjs.isDayjs(selectedDate)
    ) {
      console.warn("Invalid input for time or date");
      setTotalHours("00:00:00");
      return;
    }
    const start = dayjs(selectedDate)
      .hour(startTime.hour())
      .minute(startTime.minute());
    const end = dayjs(selectedDate)
      .hour(endTime.hour())
      .minute(endTime.minute());

    const diff = end.diff(start, "minute");
    if (diff > 0) {
      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;
      const formatted = `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:00`;
      setTotalHours(formatted);
    } else {
      setTotalHours("00:00:00");
    }
  };
  useEffect(() => {
    if (selectedDate && startTime && endTime) {
      calculateTotalHours();
    }
  }, [selectedDate, startTime, endTime]);

  const fetchData = async () => {
    try {
      const data = isAdminOrManager
        ? await getAllOvertime(empToken)
        : await getSelfOvertime(empToken);
      setTableData(
        data.map((item: OvertimeRecord, index: number) => ({
          key: item.overtime_id,
          sno: index + 1,
          employee_id: item.employee_id,
          date: dayjs(item.date_apply).format("DD-MM-YYYY"),
          startTime: item.start_time,
          endTime: item.end_time,
          totalHours: item.total_hours,
          noOfDays: item.no_of_days,
          status: item.status.toLowerCase(),
          reason: item.reason,
          rejectReason: item.reject_reason,
          approved_by: item.approved_by,
          created_at: item.created_at,
        }))
      );
      // Fetch images for all employee IDs and approved_by IDs in the table
      const uniqueIds = new Set<string>([
        ...data.map((item) => item.employee_id),
        ...data
          .map((item) => item.approved_by)
          .filter((id): id is string => typeof id === "string"),
      ]);
      const imagePromises = Array.from(uniqueIds).map((empId) =>
        fetchEmployeeImage(empId, empToken)
      );
      const images = await Promise.all(imagePromises);
      const imageMap = Object.fromEntries(
        Array.from(uniqueIds).map((empId, index) => [empId, images[index]])
      );
      setEmpImages(imageMap);
    } catch (error) {
      console.error("Failed to fetch OT data:", error);
    }
  };

  const filteredTableData = useMemo(() => {
    let filtered = tableData;

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((row) => row.status === statusFilter);
    }

    // Filter by search text - search in all fields
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((row) => {
        // Get employee name for search
        const employeeName =
          employeeNameMap[row.employee_id]?.name || row.employee_id;

        return (
          employeeName.toLowerCase().includes(searchLower) ||
          row.date.toLowerCase().includes(searchLower) ||
          row.startTime.toLowerCase().includes(searchLower) ||
          row.endTime.toLowerCase().includes(searchLower) ||
          row.totalHours.toLowerCase().includes(searchLower) ||
          row.noOfDays.toLowerCase().includes(searchLower) ||
          row.status.toLowerCase().includes(searchLower) ||
          (row.reason && row.reason.toLowerCase().includes(searchLower)) ||
          (row.rejectReason &&
            row.rejectReason.toLowerCase().includes(searchLower))
        );
      });
    }

    return filtered;
  }, [tableData, searchText, statusFilter]);

  const fetchWidgets = async () => {
    try {
      const response = await getOvertimeWidgets(empToken, {
        year: selectedYear,
        month: selectedMonth + 1,
        day: viewMode === "date" ? selectedDate?.date() : undefined,
      });
      setWidgetData(response);
    } catch (error) {
      console.error("Failed to fetch widgets", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchWidgets();
  }, [role, selectedDate, selectedMonth, selectedYear]);

  useEffect(() => {
    if (
      viewMode === "date" &&
      (!selectedDate || selectedDate.month() !== selectedMonth)
    ) {
      setSelectedDate(dayjs().year(selectedYear).month(selectedMonth).date(1));
    }
  }, [viewMode, selectedMonth, selectedYear]);

  // Admin/Manager columns
  const adminColumns = [
    {
      title: "S.No",
      dataIndex: "sno",
    },
    {
      title: "Employee",
      dataIndex: "employee_id",
      render: (employee_id: string) => {
        const emp = employeeNameMap[employee_id];
        const imageUrl =
          empImages[employee_id] ||
          `${process.env.PUBLIC_URL}/assets/img/defaultavatar.png`;
        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md">
              <img
                src={imageUrl}
                className="img-fluid rounded-circle"
                alt="/assets/img/defaultavatar.png"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "/assets/img/defaultavatar.png";
                }}
              />
            </div>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <b>{emp?.name || employee_id}</b>
              </p>
              <span className="fs-12">
                {emp?.department || "Unknown Department"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
    },
    {
      title: "No. of Days",
      dataIndex: "noOfDays",
      render: (text: string) => {
        const isFullDay = text.toLowerCase().includes("full");
        const iconClass = isFullDay ? "ti ti-sun" : "ti ti-sunset";
        const badgeColor = isFullDay
          ? "badge bg-pink-transparent"
          : "badge bg-transparent-purple";
        const label = isFullDay ? "Full Day" : "Half Day";

        return (
          <span
            className={`badge ${badgeColor} d-inline-flex align-items-center`}
          >
            <i className={`${iconClass} me-1`} />
            {label}
          </span>
        );
      },
    },
    {
      title: "Total Hours",
      dataIndex: "totalHours",
      render: (text: string) => {
        const [hrs, mins] = text.split(":");
        return `${parseInt(hrs)} hr ${parseInt(mins)} min`;
      },
      sorter: (a: any, b: any) =>
        parseFloat(a.totalHours) - parseFloat(b.totalHours),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => {
        const iconClass =
          text === "pending"
            ? "fas fa-spinner fa-spin me-2"
            : text === "approved"
            ? "fas fa-check-circle me-1"
            : "fas fa-times-circle me-1";

        const color =
          text === "pending"
            ? "#faad14"
            : text === "approved"
            ? "#52c41a"
            : "#ff4d4f";

        const label = text.charAt(0).toUpperCase() + text.slice(1);

        return (
          <span
            className="d-inline-flex align-items-center"
            style={{ color, fontWeight: 500 }}
          >
            <i className={iconClass} />
            {label}
          </span>
        );
      },
    },

    {
      title: "Reason",
      dataIndex: "reason",
      render: (text: string) => (
        <span className="text-truncate d-block" style={{ maxWidth: "200px" }}>
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      render: (text: string) => dayjs(text).format("DD-MM-YYYY hh:mm A"), // 12-hour format
    },
  ];

  // Employee columns
  const employeeColumns = [
    {
      title: "S.No",
      dataIndex: "sno",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "date",
    },
    {
      title: "Start / End Time",
      render: (_: any, record: any) => (
        <>
          <span className="badge bg-secondary-transparent me-1">
            {dayjs(record.startTime, "HH:mm:ss").format("hh:mm A")}
          </span>
          <br />
          <span className="badge bg-soft-primary">
            {dayjs(record.endTime, "HH:mm:ss").format("hh:mm A")}
          </span>
        </>
      ),
    },
    {
      title: "Total Hours",
      dataIndex: "totalHours",
      render: (text: string) => {
        const [hrs, mins] = text.split(":");
        return `${parseInt(hrs)} hr ${parseInt(mins)} min`;
      },
      sorter: (a: any, b: any) =>
        parseFloat(a.totalHours) - parseFloat(b.totalHours),
    },
    {
      title: "No. of Days",
      dataIndex: "noOfDays",
      render: (text: string) => {
        const isFullDay = text.toLowerCase().includes("full");
        const iconClass = isFullDay ? "ti ti-sun" : "ti ti-sunset";
        const badgeColor = isFullDay
          ? "badge bg-pink-transparent"
          : "badge bg-transparent-purple";
        const label = isFullDay ? "Full Day" : "Half Day";

        return (
          <span
            className={`badge ${badgeColor} d-inline-flex align-items-center`}
          >
            <i className={`${iconClass} me-1`} />
            {label}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => {
        const iconClass =
          text === "pending"
            ? "fas fa-spinner fa-spin me-2"
            : text === "approved"
            ? "fas fa-check-circle me-1"
            : "fas fa-times-circle me-1";

        const color =
          text === "pending"
            ? "#faad14" // yellow
            : text === "approved"
            ? "#52c41a" // green
            : "#ff4d4f"; // red

        const label = text.charAt(0).toUpperCase() + text.slice(1);
        return (
          <span
            className="d-inline-flex align-items-center"
            style={{ color, fontWeight: 400 }}
          >
            <i className={iconClass} />
            {label}
          </span>
        );
      },
    },
    {
      title: "Approved By",
      key: "approved_by",
      render: (_: any, record: any) => {
        const approver = employeeNameMap[record.approved_by || ""];
        const imageUrl =
          empImages[record.approved_by] ||
          `${process.env.PUBLIC_URL}/assets/img/defaultavatar.png`;

        if (!record.approved_by) {
          return <span className="text-muted">-</span>;
        }

        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md">
              <img
                src={imageUrl}
                className="img-fluid rounded-circle"
                alt={approver?.name || "Default Avatar"}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = `${process.env.PUBLIC_URL}/assets/img/defaultavatar.png`;
                }}
              />
            </div>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <b>{approver?.name || record.approved_by}</b>
              </p>
              <span className="fs-12 fw-bold">
                {approver?.role || "Unknown Role"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (_: any, record: any) =>
        record.status === "rejected" && record.rejectReason
          ? record.rejectReason
          : record.reason || "N/A",
    },
  ];

  const handleStatusChange = (record: any, status: string) => {
    setCurrentEmployee(record);
    setSelectedStatus(status);

    if (status === "rejected") {
      document.getElementById("reject_reason_trigger_btn")?.click();
    } else if (status === "approved") {
      document.getElementById("approve_confirmation_trigger_btn")?.click();
    }
  };

  const handleBulkApprove = async () => {
    try {
      const updates: OvertimeUpdate[] = selectedRowKeys.map((id) => ({
        overtime_id: Number(id),
        status: "Approved" as const, // ✅ fix here
      }));

      await Promise.all(updates.map((u) => updateOvertimeStatus(u, empToken)));

      fetchData();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("Bulk approval failed", error);
    }
  };

  const handleBulkReject = async () => {
    try {
      const updates: OvertimeUpdate[] = selectedRowKeys.map((id) => ({
        overtime_id: Number(id),
        status: "Rejected" as const, // ✅ fix here
        reject_reason: rejectReason,
      }));

      await Promise.all(updates.map((u) => updateOvertimeStatus(u, empToken)));

      fetchData();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("Bulk rejection failed", error);
    }
  };

  const handleAddOvertime = async () => {
    if (!selectedDate || !startTime || !endTime || !noOfDays || !reason) return;

    const payload: OvertimeRequest = {
      date_apply: selectedDate.format("YYYY-MM-DD"),
      start_time: startTime.format("HH:mm:ss"),
      end_time: endTime.format("HH:mm:ss"),
      total_hours: totalHours,
      no_of_days: noOfDays.value as "Full Day" | "Half Day",
      reason,
    };

    try {
      await postOvertime(payload, empToken);
      fetchData(); // Refresh table
    } catch (error) {
      console.error("Failed to submit overtime", error);
    }

    // Reset form
    setSelectedDate(null);
    setStartTime(null);
    setEndTime(null);
    setTotalHours("");
    setNoOfDays(null);
    setReason("");
    setNotify(null); // reset notify
  };

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body;
  };

  const handleNoOfDaysChange = (option: Option | null) => {
    setNoOfDays(option);
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date && date.month() !== selectedMonth) {
      toast.warn(
        "Please change the month filter before selecting date from another month."
      );
      return;
    }
    setSelectedDate(date);
  };

  // ✅ UPDATED MONTH BUTTON HANDLER
  const handleMonthClick = (idx: number) => {
    setSelectedMonth(idx);
    setSelectedDate(dayjs().year(selectedYear).month(idx));
    setIsMonthDropdownOpen(false);
  };

  const handleStatusFilterChange = (option: Option | null) => {
    setStatusFilter(option?.value || "all");
  };

  // Get appropriate columns and data based on role
  const columns = isAdminOrManager ? adminColumns : employeeColumns;

  const WidgetCard = ({
    title,
    value,
    icon,
    iconBg,
    labelTopCentre,
  }: WidgetCardProps) => {
    return (
      <div className="col-xl-3 col-md-6 position-relative">
        <div
          className="card px-4 pt-4 pb-3"
          style={{
            borderRadius: "16px",
            boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
            backgroundColor: " #fff",
          }}
        >
          {/* Floating icon box */}
          <div
            className="position-absolute"
            style={{
              top: "-16px",
              left: "20px",
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              backgroundColor: iconBg,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
              color: "#fff",
            }}
          >
            <i className={`${icon} fs-20`} />
          </div>

          {/* Optional top-right label */}
          {labelTopCentre && (
            <div
              className="position-absolute text-muted fw-semibold"
              style={{
                top: "12px",
                right: "20px",
                fontSize: "20px",
              }}
            >
              {labelTopCentre}
            </div>
          )}

          {/* Card Content */}
          <div className="pt-4">
            <p className="text-muted fw-medium mb-1">{title}</p>
            <h4 className="fw-bold text-dark mb-2">{value}</h4>
          </div>
        </div>
      </div>
    );
  };

  // ✅ FETCH WIDGET DATA ON DATE OR MONTH CHANGE
  useEffect(() => {
    fetchWidgets();
  }, [selectedDate, selectedMonth, selectedYear]);

  // ✅ STATUS DROPDOWN WITH ICON
  const StatusDropdown: React.FC<StatusDropdownProps> = ({
    value,
    onChange,
  }) => {
    const selectedLabel =
      statusOptions.find((option) => option.value === value)?.label ||
      "Filter Status";

    return (
      <Dropdown align="end">
        <Dropdown.Toggle
          variant="light"
          className="border d-flex align-items-center"
          style={{
            padding: "6px 12px",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: 500,
            backgroundColor: "#f8f9fa",
            color: "#212529",
            minWidth: "140px", // ✅ Add this line
            justifyContent: "space-between", // ✅ Ensures spacing for icon + text
          }}
        >
          <i className="ti ti-filter me-2" />
          {selectedLabel}
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {statusOptions.map((option) => (
            <Dropdown.Item
              key={option.value}
              onClick={() => onChange(option)}
              active={value === option.value}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-5">
            {/* Left: Title and Breadcrumb */}
            <div className="my-auto mb-2">
              <h2 className="mb-1">Overtime</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="index.html">
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Overtime
                  </li>
                </ol>
              </nav>
            </div>

            {/* Right: Controls */}
            <div className="d-flex flex-wrap align-items-center gap-2 mt-3 mt-md-0">
              {/* Add Overtime Button */}
              {!isAdminOrManager && (
                <Link
                  to="#"
                  data-bs-toggle="modal"
                  data-inert={true}
                  data-bs-target="#add_overtime"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Overtime
                </Link>
              )}

              {/* Admin Filters */}
              {isAdminOrManager && (
                <div className="d-flex align-items-center gap-2">
                  {/* 📌 Filter Type Switch */}
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        viewMode === "month"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setViewMode("month")}
                    >
                      Month
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        viewMode === "date"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setViewMode("date")}
                    >
                      Date
                    </button>
                  </div>

                  {/* 👇 Conditionally Render Filters Based on Mode */}
                  {viewMode === "date" ? (
                    // 📅 Date Picker
                    <div style={{ height: "36px" }}>
                      <ReactDatePicker
                        selected={selectedDate ? selectedDate.toDate() : null}
                        onChange={(date) => setSelectedDate(dayjs(date))}
                        customInput={
                          <button
                            type="button"
                            className="btn btn-primary d-flex align-items-center"
                            style={{ maxHeight: "34px", minWidth: "130px" }}
                          >
                            <i className="ti ti-calendar me-2" />
                            {selectedDate
                              ? selectedDate.format("DD-MM-YYYY")
                              : "Select Date"}
                          </button>
                        }
                        dateFormat="dd-MM-yyyy"
                        popperPlacement="bottom-start"
                      />
                    </div>
                  ) : (
                    // 🗓️ Month-Year Dropdown
                    <div
                      className="position-relative"
                      style={{ width: "160px" }}
                    >
                      <button
                        onClick={toggleMonthDropdown}
                        className="btn btn-outline-primary d-flex align-items-center justify-content-between w-100"
                        style={{ maxHeight: "34px" }}
                      >
                        <i className="ti ti-calendar-month me-2" />
                        {monthNames[selectedMonth]} - {selectedYear}
                        <i className="ti ti-chevron-down ms-auto" />
                      </button>

                      {isMonthDropdownOpen && (
                        <div
                          className="dropdown-menu show p-3 shadow position-absolute bg-white"
                          style={{
                            top: "42px",
                            minWidth: "280px",
                            zIndex: 1000,
                            borderRadius: "8px",
                          }}
                        >
                          <div className="mb-3">
                            <label className="form-label fw-semibold">
                              Select Month:
                            </label>
                            <div className="d-flex flex-wrap gap-1">
                              {monthNames.map((month, idx) => (
                                <button
                                  key={month}
                                  onClick={() => handleMonthClick(idx)}
                                  className={`btn btn-sm ${
                                    selectedMonth === idx
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  style={{
                                    fontSize: "11px",
                                    padding: "4px 8px",
                                  }}
                                >
                                  {month}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label className="form-label fw-semibold">
                              Year:
                            </label>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={selectedYear}
                              onChange={(e) => setSelectedYear(+e.target.value)}
                              min="2020"
                              max="2030"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Collapse */}
              <div>
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* /Breadcrumb */}

          {/* Overtime Counts - Only for Admin/Manager */}
          {isAdminOrManager && (
            <div className="row g-4 mb-4">
              <WidgetCard
                title="Total Count"
                value={widgetData.applied || 0}
                icon="ti ti-clipboard-list"
                iconBg="#1e1e1e" //(bg-danger-subtle)
                labelTopCentre="Applied"
              />
              <WidgetCard
                title="Total Count"
                value={widgetData.pending || 0}
                icon="ti ti-hourglass-high"
                iconBg="#b48d38ff"
                labelTopCentre="Pending"
              />
              <WidgetCard
                title="Total Count"
                value={widgetData.approved || 0}
                icon="ti ti-circle-check"
                iconBg="#28a745"
                labelTopCentre="Approved"
              />
              <WidgetCard
                title="Total Count"
                value={widgetData.rejected || 0}
                icon="ti ti-circle-x"
                iconBg="#f87b7bff"
                labelTopCentre="Rejected"
              />
            </div>
          )}

          {/* /Overtime Counts */}

          {/* Overtime Table */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              {/* Left: Title */}
              <h5 className="mb-0">
                Overtime {isAdminOrManager ? "Management" : "Requests"}
              </h5>

              {/* Right: Filter + Search */}
              <div
                className="d-flex align-items-center gap-2"
                style={{ flexWrap: "nowrap" }}
              >
                {/* Status Filter */}
                <div style={{ minWidth: "140px" }}>
                  <StatusDropdown
                    value={statusFilter}
                    onChange={(opt) => setStatusFilter(opt.value)}
                  />
                </div>

                {/* Global Actions */}
                {isAdminOrManager && (
                  <div
                    className="btn-group border rounded"
                    role="group"
                    aria-label="Action Buttons"
                    style={{ height: "32px" }}
                  >
                    {/* Approve */}
                    <button
                      type="button"
                      className="btn btn-outline-success d-flex align-items-center px-3"
                      data-bs-toggle="modal"
                      data-bs-target="#approve_confirmation_modal"
                      disabled={selectedRowKeys.length === 0} // disable if no selection
                    >
                      <i className="ti ti-circle-check me-1" />
                      Approve
                    </button>

                    {/* Reject */}
                    <button
                      type="button"
                      className="btn btn-outline-danger d-flex align-items-center px-3"
                      data-bs-toggle="modal"
                      data-bs-target="#reject_reason_modal"
                      disabled={selectedRowKeys.length === 0} // disable if no selection
                    >
                      <i className="ti ti-circle-x me-1" />
                      Reject
                    </button>
                  </div>
                )}
                {/* Search Bar */}
                <Input.Search
                  placeholder="Search records..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ maxWidth: 300 }}
                  allowClear
                />
              </div>
            </div>

            <div className="card-body p-0">
              <Table
                dataSource={filteredTableData}
                columns={columns}
                rowSelection={isAdminOrManager ? rowSelection : undefined}
                Selection={true}
              />
            </div>
          </div>
          {/* /Overtime Table */}
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Add Overtime Modal */}
      <div className="modal fade" id="add_overtime">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Overtime</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Select Date <span className="text-danger">*</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control datetimepicker"
                          format="DD-MM-YYYY"
                          value={selectedDate}
                          onChange={setSelectedDate}
                          getPopupContainer={getModalContainer}
                          placeholder="DD-MM-YYYY"
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Start Time <span className="text-danger">*</span>
                      </label>
                      <TimePicker
                        className="form-control"
                        format="hh:mm A"
                        use12Hours
                        value={startTime}
                        onChange={setStartTime}
                        getPopupContainer={getModalContainer}
                        placeholder="Select start time"
                        disabled={!selectedDate}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        End Time <span className="text-danger">*</span>
                      </label>
                      <TimePicker
                        className="form-control"
                        format="hh:mm A"
                        use12Hours
                        value={endTime}
                        onChange={setEndTime}
                        getPopupContainer={getModalContainer}
                        placeholder="Select end time"
                        disabled={!selectedDate}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Total Hours <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={totalHours}
                        onChange={(e) => setTotalHours(e.target.value)}
                        placeholder="Auto calculated"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        No. of Days <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        key={noOfDays ? noOfDays.value : "reset"}
                        className="select"
                        options={daysOptions}
                        value={noOfDays ?? undefined}
                        onChange={handleNoOfDaysChange}
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Reason <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for overtime"
                      />
                    </div>
                  </div>
                  {/* <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Notify <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        value={notify ?? ""}
                        onChange={(e) => setNotify(e.target.value)}
                      >
                        <option value="" disabled>
                          Select manager
                        </option>
                        {Object.entries(employeeNameMap)
                          .filter(([_, emp]) =>
                            emp.role.toLowerCase().includes("manager")
                          ) // ✅ lowercase
                          .map(([id, emp]) => (
                            <option key={id} value={id}>
                              {emp.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div> */}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={() => {
                    setSelectedDate(null);
                    setStartTime(null);
                    setEndTime(null);
                    setTotalHours("");
                    setNoOfDays(null);
                    setReason("");
                    // setNotify(null);
                  }}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddOvertime}
                  // data-bs-dismiss="modal"
                  disabled={
                    !selectedDate ||
                    !startTime ||
                    !endTime ||
                    !totalHours ||
                    !noOfDays ||
                    !reason
                    // !notify
                  }
                >
                  Add Overtime
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add Overtime Modal */}

      {/* Reject Reason Modal - Only for Admin/Manager */}
      {isAdminOrManager && (
        <div className="modal fade" id="reject_reason_modal">
          <div className="modal-dialog modal-md">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Reject Overtime</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="modal-body pb-0">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Reason for Rejection{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          rows={4}
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Enter reason for rejecting this overtime request"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light me-2">
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleBulkReject}
                    data-bs-dismiss="modal"
                  >
                    OK
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* /Reject Reason Modal */}

      {/* Accept Confirmation Modal - Only for Admin/Manager */}
      {isAdminOrManager && (
        <div className="modal fade" id="approve_confirmation_modal">
          <div className="modal-dialog modal-md">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Approve Overtime</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <p className="text-center">
                        Are you sure you want to approve overtime request?
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light me-2">
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleBulkApprove}
                  data-bs-dismiss="modal"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* /Accept Confirmation Modal */}

      {/* Hidden trigger buttons for modals - Only for Admin/Manager */}
      {isAdminOrManager && (
        <>
          <button
            id="reject_reason_trigger_btn"
            type="button"
            className="d-none"
            data-bs-toggle="modal"
            data-bs-target="#reject_reason_modal"
          />
          <button
            id="approve_confirmation_trigger_btn"
            type="button"
            className="d-none"
            data-bs-toggle="modal"
            data-bs-target="#approve_confirmation_modal"
          />
        </>
      )}
    </>
  );
};

export default OverTime;
